import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router";
import { ApiFailure } from "../api";

/* Gemeinsame Bausteine der Web-App. */

export function useUnauthorizedRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(
    (err: unknown) => {
      if (err instanceof ApiFailure && err.status === 401) {
        const login = location.pathname.startsWith("/desktop") ? "/desktop/anmelden" : "/anmelden";
        navigate(`${login}?weiter=${encodeURIComponent(location.pathname)}`, { replace: true });
        return true;
      }
      return false;
    },
    [navigate, location.pathname],
  );
}

export type IconName =
  | "back" | "camera" | "plus" | "file" | "folder" | "more" | "close" | "download" | "trash" | "chev" | "search"
  | "mic" | "stop" | "note" | "calendar" | "pin" | "user" | "left" | "right" | "settings" | "grid" | "send" | "lock" | "shield"
  | "pen" | "check" | "clipboard" | "eye" | "euro" | "bell" | "bank" | "cap" | "phone" | "chart" | "panel" | "clip";

export function Icon({ name }: { name: IconName }) {
  const paths: Record<IconName, ReactNode> = {
    mic: (
      <>
        <rect x="9" y="3" width="6" height="11" rx="3" />
        <path d="M5.5 11a6.5 6.5 0 0013 0M12 17.5V21" />
      </>
    ),
    stop: <rect x="7" y="7" width="10" height="10" rx="1.5" fill="currentColor" />,
    note: (
      <>
        <path d="M5 4h14v16H5z" />
        <path d="M8.5 9h7M8.5 13h7M8.5 17h4" />
      </>
    ),
    calendar: (
      <>
        <rect x="4" y="5.5" width="16" height="14.5" rx="2" />
        <path d="M4 10h16M8.5 3.5v4M15.5 3.5v4" />
      </>
    ),
    pin: (
      <>
        <path d="M12 21s-6-6.2-6-11a6 6 0 0112 0c0 4.8-6 11-6 11z" />
        <circle cx="12" cy="10" r="2.2" />
      </>
    ),
    user: (
      <>
        <circle cx="12" cy="8.5" r="3.5" />
        <path d="M5 20c1.2-3.6 3.8-5.2 7-5.2s5.8 1.6 7 5.2" />
      </>
    ),
    left: <path d="M15 5l-7 7 7 7" />,
    settings: (
      <>
        <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
        <circle cx="16" cy="7" r="2.2" />
        <circle cx="10" cy="17" r="2.2" />
      </>
    ),
    grid: (
      <>
        <rect x="4" y="4" width="7" height="7" rx="1.5" />
        <rect x="13" y="4" width="7" height="7" rx="1.5" />
        <rect x="4" y="13" width="7" height="7" rx="1.5" />
        <rect x="13" y="13" width="7" height="7" rx="1.5" />
      </>
    ),
    right: <path d="M9 5l7 7-7 7" />,
    send: <path d="M4 12l16-8-6 16-2.5-6.5z M11.5 13.5L20 4" />,
    lock: (
      <>
        <rect x="5" y="10.5" width="14" height="10" rx="2" />
        <path d="M8 10.5V8a4 4 0 018 0v2.5" />
      </>
    ),
    shield: <path d="M12 3l7 3v5.5c0 4.4-3 7.8-7 9.5-4-1.7-7-5.1-7-9.5V6z M9 12l2 2 4-4" />,
    back: <path d="M15 5l-7 7 7 7" />,
    camera: (
      <>
        <path d="M4 8h3l2-3h6l2 3h3v11H4z" />
        <circle cx="12" cy="13" r="3.6" />
      </>
    ),
    plus: <path d="M12 5v14M5 12h14" />,
    file: (
      <>
        <path d="M6 3h8l4 4v14H6z" />
        <path d="M14 3v4h4" />
      </>
    ),
    folder: <path d="M3 6h7l2 2h9v11H3z" />,
    more: (
      <>
        <circle cx="5" cy="12" r="1.3" />
        <circle cx="12" cy="12" r="1.3" />
        <circle cx="19" cy="12" r="1.3" />
      </>
    ),
    close: <path d="M6 6l12 12M18 6L6 18" />,
    clip: <path d="M20 11.5l-8 8a5 5 0 01-7-7l8.5-8.5a3.3 3.3 0 014.7 4.7L9.7 17.2a1.7 1.7 0 01-2.4-2.4L15 7" />,
    download: <path d="M12 4v11m-5-5l5 5 5-5M5 20h14" />,
    trash: <path d="M5 7h14M10 7V4h4v3m-7 0l1 13h8l1-13" />,
    chev: <path d="M9 5l7 7-7 7" />,
    pen: <path d="M4 20l1.2-4.6L16.5 4.1a2 2 0 012.8 0l.6.6a2 2 0 010 2.8L8.6 18.8z M14 6.5l3.5 3.5 M4 20h16" />,
    check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
    clipboard: (
      <>
        <path d="M9 4h6v3H9z" />
        <path d="M15 5.5h3V21H6V5.5h3 M9 12l2 2 4-4" />
      </>
    ),
    eye: (
      <>
        <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ),
    euro: <path d="M17.5 7.5A6 6 0 106 12a6 6 0 0011.5 4.5M4 10.5h9M4 13.5h9" />,
    bell: <path d="M6 17V11a6 6 0 0112 0v6l1.5 2h-15zM10 21h4" />,
    bank: <path d="M3 9.5L12 4l9 5.5M5 10v8M9.5 10v8M14.5 10v8M19 10v8M3 20h18" />,
    cap: <path d="M2.5 9.5L12 5l9.5 4.5L12 14zM6.5 11.5v4.5c1.5 1.6 3.4 2.3 5.5 2.3s4-.7 5.500-2.300v-4.500" />,
    phone: (
      <>
        <rect x="7" y="2.5" width="10" height="19" rx="2.2" />
        <path d="M11 18.5h2" />
      </>
    ),
    chart: <path d="M4 20V4M4 20h16M8 16v-4M12.500 16V8M17 16v-6" />,
    panel: (
      <>
        <rect x="3" y="4.5" width="18" height="15" rx="2.2" />
        <path d="M9.500 4.500v15" />
      </>
    ),
    search: (
      <>
        <circle cx="11" cy="11" r="6" />
        <path d="M16 16l4 4" />
      </>
    ),
  };
  return (
    <svg className="ico" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
}

export function AppBar({ title, sub, back, action }: { title: string; sub?: string; back?: string; action?: ReactNode }) {
  return (
    <header className="ab">
      {back ? (
        <Link className="ab-btn" to={back} aria-label="Zurück">
          <Icon name="back" />
        </Link>
      ) : (
        <Link className="ab-logo" to="/kundenbereich" aria-label="HandVero Kundenbereich">
          <svg viewBox="0 0 34 30" width="28" height="25" aria-hidden="true">
            <path d="M23 3H2.5V27.5H27V15" fill="none" stroke="currentColor" strokeWidth="3" />
            <path d="M6 15.5l3.8-1.3 3.7 3.6L32 1.5 13.6 25.5z" fill="currentColor" />
          </svg>
        </Link>
      )}
      <div className="ab-title">
        <b>{title}</b>
        {sub && <span>{sub}</span>}
      </div>
      {action}
    </header>
  );
}

export function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  // Über ein Portal ans Seitenende, damit kein Elternteil (Kopf, Leiste) das Fenster beschneidet.
  return createPortal(
    <div className="sheet-bg" onClick={onClose}>
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <b>{title}</b>
          <button className="ab-btn" onClick={onClose} aria-label="Schließen">
            <Icon name="close" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.querySelector(".hv-app") ?? document.body,
  );
}

export function Message({ kind = "bad", children }: { kind?: "bad" | "ok" | "info"; children: ReactNode }) {
  if (!children) return null;
  return (
    <p className={`app-msg ${kind}`} role={kind === "bad" ? "alert" : "status"}>
      {children}
    </p>
  );
}


/** „03_Fotos“ → Nummer „03“ und Name „Fotos“: Nummern werden groß gesetzt, der Name bleibt lesbar. */
export function splitNumber(name: string): { num: string | null; label: string } {
  const m = /^(\d{1,3})[_\s.-]+(.+)$/.exec(name);
  return m ? { num: m[1]!, label: m[2]! } : { num: null, label: name };
}

export const LOGO = (
  <svg viewBox="0 0 34 30" width="26" height="23" aria-hidden="true">
    <path d="M23 3H2.5V27.5H27V15" fill="none" stroke="currentColor" strokeWidth="3" />
    <path d="M6 15.5l3.8-1.3 3.7 3.6L32 1.5 13.6 25.5z" fill="currentColor" />
  </svg>
);

/**
 * Großer Kopf im Stil der Website: Kicker, riesiger Titel, gepunktetes Muster.
 * Beim Scrollen schiebt sich oben eine schmale Leiste mit dem Titel ein.
 */
export function Hero(props: { kicker: string; title: string; num?: string | null; sub?: string; back?: string; actions?: ReactNode; children?: ReactNode }) {
  const ref = useRef<HTMLElement>(null);
  const [mini, setMini] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => setMini(!e!.isIntersecting), { rootMargin: "-64px 0px 0px 0px", threshold: 0 });
    const probe = el.querySelector(".hero-title");
    if (probe) io.observe(probe);
    return () => io.disconnect();
  }, []);
  const lead = props.back ? (
    <Link className="ab-btn" to={props.back} aria-label="Zurück">
      <Icon name="back" />
    </Link>
  ) : (
    <Link className="ab-btn logo-btn" to="/kundenbereich" aria-label="HandVero Kundenbereich">
      {LOGO}
    </Link>
  );
  return (
    <>
      <div className={`mini-bar${mini ? " on" : ""}`} aria-hidden={!mini} inert={!mini}>
        {lead}
        <b>{props.title}</b>
        <span className="mini-actions">{props.actions}</span>
      </div>
      <header className="hero-app" ref={ref}>
        <div className="hero-top">
          {lead}
          <span className="hero-actions">{props.actions}</span>
        </div>
        <p className="kicker">▼ {props.kicker}</p>
        <h1 className="hero-title">
          {props.num && <span className="hero-num">{props.num}</span>}
          <span className="hero-word">{props.title}</span>
        </h1>
        {props.sub && <p className="hero-sub">{props.sub}</p>}
        {props.children}
      </header>
    </>
  );
}

/** Umschalter im Kopf (Ordner / Notizen / Termine eines Projekts). */
export function Segments({ items }: { items: { to: string; label: string; active: boolean }[] }) {
  return (
    <nav className="segs" aria-label="Bereiche des Projekts">
      {items.map((i) => (
        <Link key={i.to} to={i.to} className={i.active ? "on" : undefined} aria-current={i.active ? "page" : undefined} replace>
          {i.label}
        </Link>
      ))}
    </nav>
  );
}

/** Schwebende Leiste unten: Bereiche links, die wichtigste Aktion rechts als großer Knopf. */
export function Island({ children }: { children: ReactNode }) {
  return (
    <div className="dock-wrap">
      <nav className="dock-island">{children}</nav>
    </div>
  );
}

export function IslandTab({ to, icon, label, active }: { to: string; icon: IconName; label: string; active: boolean }) {
  return (
    <Link to={to} className={`is-tab${active ? " on" : ""}`} aria-current={active ? "page" : undefined} replace>
      <Icon name={icon} />
      <span>{label}</span>
    </Link>
  );
}

export function rootTabs(active: "projekte" | "notizen" | "kalender") {
  return (
    <>
      <IslandTab to="/app" icon="grid" label="Projekte" active={active === "projekte"} />
      <IslandTab to="/app/notizen" icon="note" label="Notizen" active={active === "notizen"} />
      <IslandTab to="/app/kalender" icon="calendar" label="Kalender" active={active === "kalender"} />
    </>
  );
}

/** Leerer Zustand mit großem Schriftzug im Stil der Website. */
export function EmptyState({ title, text, words }: { title: string; text: string; words: string[] }) {
  return (
    <div className="empty-big">
      <p className="empty-words" aria-hidden="true">
        {words.map((w, i) => (
          <span key={w} className={i === words.length - 1 ? "outline" : undefined}>
            {w}.
          </span>
        ))}
      </p>
      <b>{title}</b>
      <p>{text}</p>
    </div>
  );
}


/**
 * PDF direkt in HandVero anzeigen. Die Windows-App öffnet keine neuen Fenster, deshalb kein target="_blank".
 * Der Zeitstempel in der Adresse sorgt dafür, dass bei jedem Öffnen die frische Vorschau geladen wird.
 */
export function PdfViewer({ url, title, onClose }: { url: string; title: string; onClose: () => void }) {
  const [stamp] = useState(() => Date.now());
  const frame = useRef<HTMLIFrameElement>(null);
  const print = () => {
    try {
      frame.current?.contentWindow?.focus();
      frame.current?.contentWindow?.print();
    } catch {
      window.open(url, "_blank");
    }
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const src = `${url}${url.includes("?") ? "&" : "?"}v=${stamp}`;
  return createPortal(
    <div className="pdfv" role="dialog" aria-modal="true" aria-label={title}>
      <div className="pdfv-head">
        <b>{title}</b>
        <button className="app-btn small ghost" onClick={print}>
          Drucken
        </button>
        <a className="app-btn small ghost" href={`${url}${url.includes("?") ? "&" : "?"}download=1`} download>
          <Icon name="download" /> Speichern
        </a>
        <button className="ab-btn" onClick={onClose} aria-label="Schließen">
          <Icon name="close" />
        </button>
      </div>
      <iframe ref={frame} className="pdfv-frame" src={src} title={title} />
    </div>,
    document.querySelector(".hv-app") ?? document.body,
  );
}
